import { useState } from "react";
import DefaultProfileImg from "../assets/img/default-profile.avif";
import { useAuth } from "../context/contextAuth";
import {
  generateProfile,
  binaryToImageUrl,
  imageToBinary
} from "../utils/profilePic";
import { updateUserImg, updateUserdata } from "../api/update/update-user";
import { getUserImg, getUserData } from "../api/get/get-user";

const useProfile = () => {
  const { user } = useAuth();
  const [totalSaveScore, setTotalSaveScore] = useState("0");
  const [upgradePlan, setUpgradePlan] = useState(0);
  const [sidebarExtended, setSidebarExtended] = useState(true);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [showUpgradeCard, setShowUpgradeCard] = useState(false);
  const [showEpssCard, setShowEpssCard] = useState(false);

  const [profilePicUrl, setProfilePicUrl] = useState(null);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [profilePicBlob, setProfilePicBlob] = useState(null);

  const [selectedItem, setSelectedItem] = useState("Profile");
  const [dashboard_id, setDashboard_id] = useState("");
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [registeredRegIP, setRegisteredRegIP] = useState("");
  const [registeredUrl, setRegisteredUrl] = useState("");
  const [registeredIP, setRegisteredIP] = useState("");
  const [registeredAws, setRegisteredAws] = useState(false);
  const [registeredAzure, setRegisteredAzure] = useState(false);
  const [registeredGCloud, setRegisteredGCloud] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [copyIp, setCopyIp] = useState(false);
  const [copyDId, setCopyDId] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loadingSave, setLoadingSave] = useState(false);

  const initData = async () => {
    if (!user) {
      resetState();
      return;
    }

    await loadProfile();
    await loadProfilePicture();
  };

  const loadProfile = async () => {
    setDashboard_id(user?.d_id || "");
    setUsername(user?.username || "");
    setName(`${user?.first_name} ${user?.last_name}` || "");
    setCompanyName(user?.company || "");
    setEmail(user?.email || "");
    setRegisteredRegIP(user?.public_ip_address || "");
  };

  const loadProfilePicture = async () => {
    try {
      const img = user?.profile_image;  
      if (!img || img === "null" || img === "" || img === undefined || img === null) {
        if (user?.company) {
          const generated = generateProfile(user.company);
          setProfilePicUrl(generated);
          return;
        }
  
        setProfilePicUrl(DefaultProfileImg);
        return;
      }
  
      const url = binaryToImageUrl(img);
      setProfilePicUrl(url);
    } catch (err) {
      console.error("Error loading profile picture:", err);
      setProfilePicUrl(DefaultProfileImg);
    }
  };

  const handleSaveProfile = async () => {
    if (!profilePicBlob) {
      // console.log("Please select an image first.");
      alert("Please select an image first.");
      return;
    }
  
    const updatedData = {
      userId: user?.id,
      profileImage: profilePicBlob,
    };
  
    try {
      const response = await updateUserImg(updatedData);
      if (response?.success) {
        const updatedUser = await getUserImg(user.id);
        if (updatedUser?.profile_image) {
          const url = binaryToImageUrl(updatedUser.profile_image);
          setProfilePicUrl(url);
        }
      }
    } catch (error) {
      console.error("Save error:", error);
    }
  };  

  const handleSaveData = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const updatedData = { oldPassword, newPassword };
    setLoadingSave(true);
    try {
      const response = await updateUserdata(user?.id, updatedData);
      setSuccess(response?.message || "Password updated successfully!");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      await getUserData(user?.id);
    } catch (error) {
      const backendError =
        error?.response?.data?.error ||
        error?.error ||
        "Failed to update password";
      setError(backendError);
    } finally {
      setLoadingSave(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    setProfilePicUrl(URL.createObjectURL(file));
  
    const binary = await imageToBinary(file);
    const blob = new Blob([binary], { type: file.type });
  
    setProfilePicBlob(blob);
    setProfilePicFile(file);
  };

  const resetState = () => {
    setTotalSaveScore("0");
    setUpgradePlan(0);
    setSidebarExtended(true);
    setShowProfileCard(false);
    setShowUpgradeCard(false);
    setShowEpssCard(false);
    setProfilePicUrl(null);
    setProfilePicFile(null);
    setProfilePicBlob(null);
    setSelectedItem("Profile");
    setDashboard_id("");
    setUsername("");
    setName("");
    setCompanyName("");
    setEmail("");
    setRegisteredRegIP("");
    setRegisteredUrl("");
    setRegisteredIP("");
    setRegisteredAws(false);
    setRegisteredAzure(false);
    setRegisteredGCloud(false);
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setCopyIp(false);
    setCopyDId(false);
    setIsHovered(false);
    setError("");
    setSuccess("");
    setLoadingSave(false);
  };

  return {
    initData,
    // loadProfile,
    loadProfilePicture,
    handleSaveProfile,
    handleSaveData,
    handleFileSelect,
    resetState,

    totalSaveScore, setTotalSaveScore,
    upgradePlan, setUpgradePlan,
    sidebarExtended, setSidebarExtended,
    showProfileCard, setShowProfileCard,
    showUpgradeCard, setShowUpgradeCard,
    showEpssCard, setShowEpssCard,

    profilePicUrl, setProfilePicUrl,
    profilePicFile, setProfilePicFile,
    profilePicBlob, setProfilePicBlob,

    selectedItem, setSelectedItem,
    dashboard_id, setDashboard_id,
    username, setUsername,
    name, setName,
    companyName, setCompanyName,
    email, setEmail,
    registeredRegIP, setRegisteredRegIP,
    registeredUrl, setRegisteredUrl,
    registeredIP, setRegisteredIP,
    registeredAws, setRegisteredAws,
    registeredAzure, setRegisteredAzure,
    registeredGCloud, setRegisteredGCloud,
    oldPassword, setOldPassword,
    newPassword, setNewPassword,
    confirmPassword, setConfirmPassword,
    copyIp, setCopyIp,
    copyDId, setCopyDId,
    isHovered, setIsHovered,

    error, setError,
    success, setSuccess,
    loadingSave, setLoadingSave,
  };
};

export default useProfile;
